import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { ApiUtils } from '@shared/utils';
import { CardsApi, RequestStrategyEnum, type ApiPaginate, type CardResponse, type CardsRequest, type WithSignal } from '@shared/api';

const getCardsEffect = createEffect(({ signal, ...request }: WithSignal<CardsRequest> = {}) => CardsApi.getCards(request, signal));

const clearEvent = createEvent();
const refetchEvent = createEvent();

const $lastRequestStore = createStore<CardsRequest | null>(null)
  .on(getCardsEffect, (_, params) => {
    if (!params) return null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { signal: __, ...request } = params;
    return request;
  })
  .reset(clearEvent);

const $dataStore = createStore<ApiPaginate<CardResponse[]> | null>(null)
  .on(getCardsEffect.done, (_, data) => data.result || null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardsEffect.pending, (_, data) => data)
  .on(getCardsEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getCardsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardsEffect, () => null)
  .on(getCardsEffect.fail, () => 'Something went wrong while fetching Cards. Please try again.')
  .on(getCardsEffect.done, () => null)
  .reset(clearEvent);

const getCardsApi = ApiUtils.wrapperApi(getCardsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getCardsApi.cancel());

sample({
  clock: refetchEvent,
  source: $lastRequestStore,
  filter: (request): request is CardsRequest => request !== null,
  target: getCardsApi.trigger,
});

export const $cardListStore = {
  events: {
    getCards: getCardsApi.trigger,
    refetch: refetchEvent,
    clear: clearEvent,
  },
  clear: clearEvent,
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
